<template>
    <div class="forum-wrapper">
      <div class="forum-container">
        <!-- Header -->
        <header class="forum-header">
          <h1 class="typo-headline-4">Buscar en el Foro</h1>
        </header>
  
        <!-- Search Form -->
        <section class="search-form">
          <form @submit.prevent="newSearch">
            <div class="form-group">
              <label for="keyword" class="typo-body-2">Palabra clave:</label>
              <input type="text" id="keyword" v-model="keyword" class="typo-body-2"
                placeholder="Introduce la frase de búsqueda" required />
            </div>
  
            <div class="form-group">
              <label for="section" class="typo-body-2">Categoría del foro:</label>
              <select id="section" v-model="selectedSection" class="typo-body-2">
                <option value="">Todas las categorías</option>
                <option v-for="section in sections" :key="section.tid" :value="section.tid">
                  {{ section.name }}
                </option>
              </select>
            </div>
  
            <div class="form-group">
              <label class="typo-body-2">
                <input type="checkbox" v-model="exact" />
                Búsqueda exacta
              </label>
              <small class="typo-caption">
                (Marcar para buscar la frase exacta completa. Si no se marca, se
                buscará cada palabra de la frase en cualquier parte del texto.)
              </small>
            </div>
  
            <!-- These checkboxes modify the query only; the displayed results remain unchanged until a new search is executed -->
            <div class="form-group">
              <label class="typo-body-2">
                <input type="checkbox" v-model="includeTopics" />
                Incluir Temas
              </label>
            </div>
            <div class="form-group">
              <label class="typo-body-2">
                <input type="checkbox" v-model="includeComments" />
                Incluir Respuestas
              </label>
            </div>
  
            <div class="form-group">
              <button type="submit" class="typo-button">Buscar</button>
            </div>
          </form>
        </section>
  
        <!-- Search Results -->
        <section class="search-results" v-if="searched">
          <div v-if="loading" class="loading-message typo-body-2">
            Buscando...
          </div>
          <div v-else-if="error" class="error-message typo-body-2">
            Error al realizar la búsqueda.
          </div>
          <div v-else>
            <!-- Combined Header when both Topics and Comments are enabled -->
            <div v-if="searchIncludeTopics && searchIncludeComments">
              <h2 class="typo-headline-6">
                <span v-if="currentPage < topicPages">Temas</span>
                <span v-else>Respuestas</span>
                ({{ combinedCount }})
              </h2>
            </div>
            <!-- Otherwise, show individual header -->
            <div v-else-if="searchIncludeTopics">
              <h2 class="typo-headline-6">Temas ({{ totalTopics }})</h2>
            </div>
            <div v-else-if="searchIncludeComments">
              <h2 class="typo-headline-6">Respuestas ({{ totalComments }})</h2>
            </div>
  
            <!-- Display results based on phased pagination:
                 When both are enabled, topics are shown on pages less than topicPages,
                 and responses are shown on pages >= topicPages. -->
            <div v-if="searchIncludeTopics && searchIncludeComments">
              <div v-if="currentPage < topicPages">
                <div v-if="resultsTopics.length">
                  <div v-for="topic in resultsTopics" :key="topic.nid" class="result-item"
                    @click="goToThread(topic)">
                    <h3 class="typo-headline-6" v-html="highlightText(topic.title)"></h3>
                    <div class="result-meta typo-caption">
                      <span>{{ formatTimestamp(topic.created) }}</span>
                    </div>
                    <p class="typo-body-2" v-html="highlightText(topic.body)"></p>
                  </div>
                </div>
                <div v-else class="typo-body-2">No se encontraron temas.</div>
              </div>
              <div v-else>
                <div v-if="resultsComments.length">
                  <div v-for="comment in resultsComments" :key="comment.cid" class="result-item"
                    @click="goToThread(comment)">
                    <h3 class="typo-headline-6" v-html="highlightText(comment.subject)"></h3>
                    <div class="result-meta typo-caption">
                      <span>{{ formatTimestamp(comment.created) }}</span>
                    </div>
                    <p class="typo-body-2" v-html="highlightText(comment.body)"></p>
                  </div>
                </div>
                <div v-else class="typo-body-2">No se encontraron respuestas.</div>
              </div>
            </div>
            <!-- When only one type is enabled, show that section -->
            <div v-else-if="searchIncludeTopics">
              <div v-if="resultsTopics.length">
                <div v-for="topic in resultsTopics" :key="topic.nid" class="result-item"
                  @click="goToThread(topic)">
                  <h3 class="typo-headline-6" v-html="highlightText(topic.title)"></h3>
                  <div class="result-meta typo-caption">
                    <span>{{ formatTimestamp(topic.created) }}</span>
                  </div>
                  <p class="typo-body-2" v-html="highlightText(topic.body)"></p>
                </div>
              </div>
              <div v-else class="typo-body-2">No se encontraron temas.</div>
            </div>
            <div v-else-if="searchIncludeComments">
              <div v-if="resultsComments.length">
                <div v-for="comment in resultsComments" :key="comment.cid" class="result-item"
                  @click="goToThread(comment)">
                  <h3 class="typo-headline-6" v-html="highlightText(comment.subject)"></h3>
                  <div class="result-meta typo-caption">
                    <span>{{ formatTimestamp(comment.created) }}</span>
                  </div>
                  <p class="typo-body-2" v-html="highlightText(comment.body)"></p>
                </div>
              </div>
              <div v-else class="typo-body-2">No se encontraron respuestas.</div>
            </div>
  
            <!-- Pagination Controls -->
            <div class="results-pagination">
              <button @click="prevPage" :disabled="currentPage === 0" class="pagination-btn typo-button">
                Anterior
              </button>
              <span class="page-info typo-body-2">
                Página {{ currentPage + 1 }} de {{ totalPages }}
              </span>
              <button @click="nextPage" :disabled="currentPage + 1 >= totalPages"
                class="pagination-btn typo-button">
                Siguiente
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  </template>
  
  <script>
  import axios from "axios";
  export default {
    name: "ForumSearch",
    data() {
      return {
        // Live form fields.
        keyword: "",
        selectedSection: "",
        exact: false,
        includeTopics: true,
        includeComments: true,
        // Stored search criteria (copied when the user clicks "Buscar")
        searchedKeyword: "",
        searchedSection: "",
        searchExact: false,
        searchIncludeTopics: true,
        searchIncludeComments: true,
        currentPage: 0, // 0 = first page.
        // Results from the backend.
        resultsTopics: [],
        resultsComments: [],
        totalTopics: 0,
        totalComments: 0,
        totalPages: 0,
        batchSize: 10,
        // List of forum sections.
        sections: [],
        // Status flags.
        loading: false,
        error: null,
        searched: false // Indicates that a search has been performed.
      };
    },
    computed: {
      // Compute the number of topic pages based on totalTopics.
      topicPages() {
        return Math.ceil(this.totalTopics / this.batchSize);
      },
      // Combined total count when both types are enabled.
      combinedCount() {
        if (this.searchIncludeTopics && this.searchIncludeComments) {
          return parseInt(this.totalTopics, 10) + parseInt(this.totalComments, 10);
        } else if (this.searchIncludeTopics) {
          return this.totalTopics;
        } else if (this.searchIncludeComments) {
          return this.totalComments;
        }
        return 0;
      }
    },
    mounted() {
      this.fetchSections();
    },
    methods: {
      // Fetch forum sections.
      fetchSections() {
        axios
          .get("/forum/section-ids")
          .then(response => {
            this.sections = response.data;
          })
          .catch(err => {
            console.error("Error fetching forum sections", err);
          });
      },
      // Copy current form values into stored search criteria and reset page.
      newSearch() {
        this.currentPage = 0;
        this.searchedKeyword = this.keyword;
        this.searchedSection = this.selectedSection;
        this.searchExact = this.exact;
        this.searchIncludeTopics = this.includeTopics;
        this.searchIncludeComments = this.includeComments;
        this.searchForum();
      },
      // Send the search query using stored criteria.
      searchForum() {
        this.loading = true;
        this.error = null;
        this.searched = false;
        const formData = new FormData();
        formData.append('keyword', this.searchedKeyword);
        formData.append('id', this.searchedSection);
        formData.append('exact', this.searchExact);
        formData.append('page', this.currentPage);
        formData.append('topics', this.searchIncludeTopics);
        formData.append('comments', this.searchIncludeComments);
  
        axios
          .post("/forum/api/search", formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
          .then(response => {
            // Expecting keys: topics, comments, totalTopics, totalComments.
            this.resultsTopics = response.data.topics || [];
            this.resultsComments = response.data.comments || [];
            this.totalTopics = response.data.totalTopics || 0;
            this.totalComments = response.data.totalComments || 0;
            // Compute total pages.
            if (this.searchIncludeTopics && this.searchIncludeComments) {
              const topicPages = Math.ceil(this.totalTopics / this.batchSize);
              const commentPages = Math.ceil(this.totalComments / this.batchSize);
              this.totalPages = topicPages + commentPages;
            } else if (this.searchIncludeTopics) {
              this.totalPages = Math.ceil(this.totalTopics / this.batchSize);
            } else if (this.searchIncludeComments) {
              this.totalPages = Math.ceil(this.totalComments / this.batchSize);
            } else {
              this.totalPages = 0;
            }
            this.searched = true;
          })
          .catch(err => {
            this.error = err;
            console.error("Error during search", err);
          })
          .finally(() => {
            this.loading = false;
          });
      },
      // Next page.
      nextPage() {
        if (this.currentPage + 1 < this.totalPages) {
          this.currentPage++;
          this.searchForum();
        }
      },
      // Previous page.
      prevPage() {
        if (this.currentPage > 0) {
          this.currentPage--;
          this.searchForum();
        }
      },
      // Format Unix timestamp (seconds) into a locale date string.
      formatTimestamp(ts) {
        const date = new Date(ts * 1000);
        return date.toLocaleString();
      },
      // Redirect to thread view.
      goToThread(item) {
        this.$router.push({ name: "ForumThread", params: { nid: item.nid } });
      },
      // Escape RegExp special characters.
      escapeRegExp(string) {
        return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      },
      // Highlight occurrences of the searched keyword(s) using the stored criteria.
      highlightText(text) {
        if (!this.searchedKeyword || !text) {
          return text;
        }
        let regex;
        if (this.searchExact) {
          regex = new RegExp("(" + this.escapeRegExp(this.searchedKeyword) + ")", "gi");
        } else {
          const words = this.searchedKeyword.split(/\s+/).filter(word => word.length > 0);
          if (words.length === 0) return text;
          regex = new RegExp("(" + words.map(word => this.escapeRegExp(word)).join("|") + ")", "gi");
        }
        return text.replace(regex, '<span class="highlight">$1</span>');
      }
    }
  };
  </script>
  
  <style scoped>
  /* Overall container */
  .forum-wrapper {
    background-color: var(--color-background);
    min-height: 100vh;
    padding: 40px 20px;
  }
  
  /* Centered container */
  .forum-container {
    background-color: var(--color-surface);
    color: var(--color-text-primary);
    max-width: 800px;
    margin: 0 auto;
    padding: 30px;
    border-radius: 8px;
    box-shadow: 0 2px 6px var(--color-outline);
  }
  
  /* Header */
  .forum-header {
    text-align: center;
    margin-bottom: 20px;
    border-bottom: 1px solid var(--color-outline);
    padding-bottom: 10px;
  }
  
  /* Search Form */
  .search-form {
    margin-bottom: 30px;
  }
  
  .form-group {
    margin-bottom: 15px;
  }
  
  input[type="text"],
  select {
    width: 100%;
    padding: 8px;
    border: 1px solid var(--color-outline);
    border-radius: 4px;
    background-color: var(--color-background);
    color: var(--color-text-primary);
  }
  
  input[type="text"] {
    padding-left: 0;
    padding-right: 0;
    text-indent: 8px;
  }
  
  input[type="checkbox"] {
    margin-right: 5px;
  }
  
  /* Button styles */
  button.typo-button {
    background-color: var(--color-background);
    color: var(--color-text-primary);
    border: none;
    padding: 10px 20px;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.3s ease;
  }
  
  button.typo-button:hover {
    background-color: var(--color-highlight);
  }
  
  /* Search results */
  .search-results h2 {
    margin-top: 20px;
    margin-bottom: 10px;
  }
  
  .result-item {
    padding: 15px;
    border-bottom: 1px solid var(--color-outline);
    cursor: pointer;
    transition: background-color 0.2s ease;
  }
  
  .result-item:hover {
    background-color: var(--color-active-surface);
  }
  
  .result-meta {
    margin-bottom: 10px;
  }
  
  /* Pagination Controls */
  .results-pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 15px;
    margin-top: 20px;
  }
  
  .pagination-btn {
    background-color: var(--color-primary);
    color: var(--color-text-primary);
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    transition: background-color 0.3s ease;
  }
  
  .pagination-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  .pagination-btn:hover:not(:disabled) {
    background-color: var(--color-highlight);
  }
  
  .page-info {
    font-size: 0.95rem;
  }
  
  /* Deep selector for injected HTML (v-html) so that .highlight is applied */
  >>> .highlight {
    background-color: var(--color-highlight);
    color: var(--color-background);
  }
  </style>
  